import * as RadixPopover from '@radix-ui/react-popover';
import { type ComponentProps, forwardRef } from 'react';

import type { DropdownContextProviderProps } from '~/components/Dropdown/contexts/DropdownContextProvider';
import { DropdownContextProvider } from '~/components/Dropdown/contexts/DropdownContextProvider';
import { useDropdownContext } from '~/components/Dropdown/hooks/useDropdownContext';

import { assertMultiSelect } from './assertions/multiSelect';
import { assertSingleSelect } from './assertions/singleSelect';
import { DropdownContent } from './components/DropdownContent';
import { DropdownInput } from './components/DropdownInput/DropdownInput';
import { DropdownTrigger } from './components/DropdownTrigger/DropdownTrigger';

interface DropdownWithoutProviderProps {
  placeholder?: string;
  state?: ComponentProps<typeof DropdownTrigger>['state'];
  size?: ComponentProps<typeof DropdownTrigger>['size'];
  children?: React.ReactNode;
  contentClassName?: string;
  scrollableContentClassName?: string;
  alignment?: 'end' | 'start' | 'center' | undefined;
  name?: string;
  disabled?: boolean;
  onClose?: () => void;
  clearable?: boolean;
  loading?: boolean;
  limitDisplayRows?: boolean;
}

function DropdownWithoutProvider({
  placeholder,
  state = 'default',
  size,
  children,
  contentClassName = '',
  scrollableContentClassName = 'max-h-[20rem]',
  alignment,
  name,
  disabled = false,
  clearable,
  onClose,
}: DropdownWithoutProviderProps) {
  const { isOpen, setIsOpen } = useDropdownContext();

  return (
    <>
      <DropdownInput invalid={state === 'error'} />
      <RadixPopover.Root open={isOpen} onOpenChange={setIsOpen}>
        <RadixPopover.Trigger asChild>
          <DropdownTrigger
            name={name}
            size={size}
            state={state}
            disabled={disabled}
            placeholder={placeholder}
            onClose={onClose}
            clearable={clearable}
          />
        </RadixPopover.Trigger>
        <RadixPopover.Portal>
          <RadixPopover.Content align={alignment} className={contentClassName}>
            <DropdownContent
              className={scrollableContentClassName}
              data-testid="dropdown-content"
            >
              {children}
            </DropdownContent>
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>
    </>
  );
}

DropdownWithoutProvider.displayName = 'DropdownWithoutProvider';

export type DropdownProps<
  T extends string,
  TMultiselect extends boolean | undefined,
> = Omit<DropdownContextProviderProps<T, TMultiselect>, 'children'> &
  DropdownWithoutProviderProps;

function DropdownWithoutRef<
  T extends string = string,
  TMultiselect extends boolean | undefined = false,
>(
  {
    options,
    filteredOptions,
    multiselect,
    displaySelectedOptions,
    onChange,
    value,
    maxSelectedOptions,
    disabled,
    loadMoreDistance,
    onLoadMore,
    onClose,
    loading,
    noOptionsText,
    limitDisplayRows,
    ...props
  }: DropdownProps<T, TMultiselect>,
  ref: React.Ref<HTMLInputElement>,
) {
  assertSingleSelect({
    multiselect,
    value,
    displaySelectedOptions,
    maxSelectedOptions,
  });
  assertMultiSelect({ multiselect, value, maxSelectedOptions });

  return (
    <DropdownContextProvider
      options={options}
      filteredOptions={filteredOptions}
      multiselect={multiselect}
      displaySelectedOptions={displaySelectedOptions}
      onChange={onChange}
      value={value}
      inputRef={ref}
      maxSelectedOptions={maxSelectedOptions}
      loadMoreDistance={loadMoreDistance}
      onLoadMore={onLoadMore}
      noOptionsText={noOptionsText}
      loading={loading}
      limitDisplayRows={limitDisplayRows}
    >
      <DropdownWithoutProvider {...{ ...props, onClose, disabled }} />
    </DropdownContextProvider>
  );
}

DropdownWithoutRef.displayName = 'Dropdown';

export const Dropdown = forwardRef(DropdownWithoutRef) as <
  T extends string = string,
  TMultiselect extends boolean | undefined = false,
>(
  props: DropdownProps<T, TMultiselect> & {
    ref?: React.ForwardedRef<HTMLInputElement>;
  },
) => ReturnType<typeof DropdownWithoutRef<T, TMultiselect>>;
